import React, { useEffect, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import type { MenuImageBannerSlide } from 'src/components/menu-image-banner';
import { PBB_NAME } from 'src/payments/constants';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useIncentives, UserType } from 'src/payments/hooks/use-incentives/use-incentives';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { usePayByBankPointsBoost } from 'src/payments/hooks/use-pay-by-bank-points-boost';
import { usePointsBoostSurfacesConfig } from 'src/payments/hooks/use-points-boost-surfaces-config';
import { IncentiveHomeBanner } from 'src/payments/components/incentive-home-banner';

export const TEST_ID_POINTS_BOOST_HOME_BANNER = 'points-boost-home-banner';

const EXPOSURE_VISIBILITY_THRESHOLD = 0.5;

export type PointsBoostHomeBannerProps = {
  multiplierLabel: string;
  isEnrolled: boolean;
  userType: UserType;
  onLinkBank: () => void;
};

export const usePointsBoostHomeBannerSlide = (): MenuImageBannerSlide | null => {
  const UI = useUI();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const { dispensary } = useDispensary();
  const { homeBanner: pointsBoostHomeBannerEnabled } = usePointsBoostSurfacesConfig();
  const { isEnrolledDutchiePay, isDutchiePayEnabledForDispo } = useDutchiePay();
  const { openDutchiePay } = useOpenDutchiePay({ enrollmentSource: `home-page-${String(UI?.variant)}` });
  const { userType } = useIncentives();
  const { status, multiplier, multiplierLabel } = usePayByBankPointsBoost({
    dispensaryId: dispensary?.id,
    enabled: pointsBoostHomeBannerEnabled && isLoggedIn && isDutchiePayEnabledForDispo,
  });

  const surfaceSuppressed = UI.isKiosk || UI.isMobileEcommApp;
  const surfaceEligible =
    pointsBoostHomeBannerEnabled && isLoggedIn && isDutchiePayEnabledForDispo && !surfaceSuppressed;

  if (!surfaceEligible || status !== 'available' || multiplier === null || multiplierLabel === null) {
    return null;
  }

  return {
    id: TEST_ID_POINTS_BOOST_HOME_BANNER,
    content: (
      <PointsBoostHomeBanner
        multiplierLabel={multiplierLabel}
        isEnrolled={isEnrolledDutchiePay}
        userType={userType}
        onLinkBank={openDutchiePay}
      />
    ),
  };
};

export const PointsBoostHomeBanner = ({
  multiplierLabel,
  isEnrolled,
  userType,
  onLinkBank,
}: PointsBoostHomeBannerProps): JSX.Element => {
  const isActionable = !isEnrolled;
  const bannerRef = useRef<HTMLElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const visibilityEntry = useIntersectionObserver(bannerRef, {
    threshold: EXPOSURE_VISIBILITY_THRESHOLD,
    freezeOnceVisible: hasBeenVisible,
  });

  useEffect(() => {
    if (
      !hasBeenVisible &&
      visibilityEntry?.isIntersecting &&
      visibilityEntry.intersectionRatio >= EXPOSURE_VISIBILITY_THRESHOLD
    ) {
      setHasBeenVisible(true);
    }
  }, [hasBeenVisible, visibilityEntry?.intersectionRatio, visibilityEntry?.isIntersecting]);

  useIncentiveExposure({
    incentiveValue: hasBeenVisible ? multiplierLabel : undefined,
    userType,
    isCashBack: false,
    source: 'home_banner',
    rewardType: 'points_boost',
  });

  return (
    <IncentiveHomeBanner
      headline={
        <>
          Get <u>{multiplierLabel} points</u> on all purchases with {PBB_NAME}!
        </>
      }
      body={<>Link your bank, and get {multiplierLabel} Points every time you pay ahead with your bank.</>}
      ctaLabel='Link your bank now'
      isActionable={isActionable}
      onCta={onLinkBank}
      ctaAriaLabel={`Link your bank to start earning ${multiplierLabel} points with ${PBB_NAME}`}
      ariaLabel={`${PBB_NAME} points boost promotion`}
      testId={TEST_ID_POINTS_BOOST_HOME_BANNER}
      customizationPrefix='points-boost'
      bannerRef={bannerRef}
    />
  );
};
